import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Navbar } from "@/components/Navbar";
import { Badge } from "@/components/ui/badge";
import { Calendar as CalendarIcon, Clock, Users, BookOpen, ArrowLeft, Loader2, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookClassPage() {
  const { classId } = useParams();
  const navigate = useNavigate();
  const [cls, setCls] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ student_name: "", student_email: "", parent_name: "", parent_email: "", phone: "" });
  const [acceptedTac, setAcceptedTac] = useState(false);
  const [tacExpanded, setTacExpanded] = useState(false);

  useEffect(() => {
    axios.get(`${API}/classes/${classId}`).then((r) => setCls(r.data)).catch(() => {
      toast.error("Class not found");
      navigate("/");
    }).finally(() => setLoading(false));
  }, [classId, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.student_name || !form.student_email) {
      toast.error("Please fill in student name and email");
      return;
    }
    if (!acceptedTac) {
      toast.error("Please accept the Terms & Conditions");
      return;
    }
    setSubmitting(true);
    try {
      const bookingRes = await axios.post(`${API}/bookings`, { class_id: classId, ...form });
      const checkoutRes = await axios.post(`${API}/payments/checkout`, {
        booking_id: bookingRes.data.id,
        origin_url: window.location.origin,
      });
      if (checkoutRes.data.url) {
        window.location.href = checkoutRes.data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create booking");
      setSubmitting(false);
    }
  };

  const update = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-24 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-brand-purple" /></div>
      </div>
    );
  }

  if (!cls) return null;

  const spotsLeft = cls.max_occupancy - cls.current_occupancy;

  return (
    <div className="min-h-screen bg-white dark:bg-[#0f1117] transition-colors duration-300" data-testid="book-class-page">
      <Navbar />
      <div className="pt-24 pb-16 px-6">
        <div className="max-w-4xl mx-auto">
          <button onClick={() => navigate("/")} className="flex items-center gap-2 text-slate-500 dark:text-slate-400 hover:text-brand-purple dark:hover:text-brand-purple-light font-body text-sm mb-8 transition-colors" data-testid="back-btn">
            <ArrowLeft className="h-4 w-4" /> Back to schedule
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* Class details */}
            <div className="lg:col-span-2">
              <div className="bg-white dark:bg-[#1a1b23] rounded-2xl border border-slate-100 dark:border-slate-800 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-6 sticky top-24 transition-colors duration-300" data-testid="class-details-card">
                <Badge className="bg-brand-purple-soft text-brand-purple font-body text-xs rounded-full mb-4">{cls.subject_name}</Badge>
                <h1 className="font-heading text-2xl font-bold text-slate-900 dark:text-white mb-2">{cls.title}</h1>
                {cls.description && <p className="text-sm text-slate-500 dark:text-slate-400 font-body mb-4">{cls.description}</p>}
                <div className="space-y-3 text-sm font-body">
                  <div className="flex items-center gap-3 text-slate-600">
                    <CalendarIcon className="h-4 w-4 text-brand-purple" /><span>{cls.date}</span>
                  </div>
                  <div className="flex items-center gap-3 text-slate-600">
                    <Clock className="h-4 w-4 text-brand-purple" /><span>{cls.start_time} - {cls.end_time}</span>
                  </div>
                  <div className="flex items-center gap-3 text-slate-600">
                    <BookOpen className="h-4 w-4 text-brand-purple" /><span>{cls.tutor_name}</span>
                  </div>
                  <div className="flex items-center gap-3 text-slate-600">
                    <Users className="h-4 w-4 text-brand-purple" />
                    <span className={spotsLeft <= 3 ? "text-amber-600 font-semibold" : ""}>{spotsLeft} spot{spotsLeft !== 1 ? 's' : ''} remaining</span>
                  </div>
                  {cls.year_level && (
                    <div className="flex items-center gap-3 text-slate-600">
                      <span className="text-brand-purple font-semibold">Level:</span><span>{cls.year_level}</span>
                    </div>
                  )}
                </div>
                <div className="mt-6 pt-4 border-t border-slate-100">
                  <p className="font-heading text-3xl font-bold text-brand-purple">${cls.price.toFixed(2)} <span className="text-sm font-body text-slate-400 font-normal">+ GST AUD</span></p>
                  <p className="text-xs text-slate-400 font-body mt-1" data-testid="book-gst-total">${(cls.price * 1.1).toFixed(2)} charged incl. GST</p>
                </div>
              </div>
            </div>

            {/* Booking form */}
            <div className="lg:col-span-3">
              <div className="bg-white dark:bg-[#1a1b23] rounded-2xl border border-slate-100 dark:border-slate-800 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8 transition-colors duration-300" data-testid="booking-form">
                <h2 className="font-heading text-xl font-bold text-slate-900 dark:text-white mb-6">Student Details</h2>
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="student_name" className="font-body text-sm text-slate-700 mb-1.5 block">Student Name *</Label>
                      <Input id="student_name" value={form.student_name} onChange={update("student_name")} required
                        className="font-body bg-white border-slate-200 focus:border-brand-purple focus:ring-2 focus:ring-brand-purple/20 rounded-lg"
                        placeholder="Full name" data-testid="input-student-name" />
                    </div>
                    <div>
                      <Label htmlFor="student_email" className="font-body text-sm text-slate-700 mb-1.5 block">Student Email *</Label>
                      <Input id="student_email" type="email" value={form.student_email} onChange={update("student_email")} required
                        className="font-body bg-white border-slate-200 focus:border-brand-purple focus:ring-2 focus:ring-brand-purple/20 rounded-lg"
                        placeholder="student@email.com" data-testid="input-student-email" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="parent_name" className="font-body text-sm text-slate-700 mb-1.5 block">Parent/Guardian Name</Label>
                      <Input id="parent_name" value={form.parent_name} onChange={update("parent_name")}
                        className="font-body bg-white border-slate-200 focus:border-brand-purple focus:ring-2 focus:ring-brand-purple/20 rounded-lg"
                        placeholder="Optional" data-testid="input-parent-name" />
                    </div>
                    <div>
                      <Label htmlFor="parent_email" className="font-body text-sm text-slate-700 mb-1.5 block">Parent/Guardian Email</Label>
                      <Input id="parent_email" type="email" value={form.parent_email} onChange={update("parent_email")}
                        className="font-body bg-white border-slate-200 focus:border-brand-purple focus:ring-2 focus:ring-brand-purple/20 rounded-lg"
                        placeholder="Optional" data-testid="input-parent-email" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="phone" className="font-body text-sm text-slate-700 mb-1.5 block">Phone Number</Label>
                    <Input id="phone" value={form.phone} onChange={update("phone")}
                      className="font-body bg-white border-slate-200 focus:border-brand-purple focus:ring-2 focus:ring-brand-purple/20 rounded-lg"
                      placeholder="04XX XXX XXX" data-testid="input-phone" />
                  </div>
                  <div className="pt-4">
                    {/* Terms & Conditions */}
                    <div className="mb-4 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden" data-testid="tac-section">
                      <button type="button" onClick={() => setTacExpanded(!tacExpanded)}
                        className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                        data-testid="tac-toggle">
                        <span className="font-body text-sm font-semibold text-slate-700 dark:text-slate-300">Terms & Conditions</span>
                        {tacExpanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                      </button>
                      {tacExpanded && (
                        <div className="px-4 pb-4 max-h-64 overflow-y-auto text-xs text-slate-600 dark:text-slate-400 font-body space-y-3 border-t border-slate-100 dark:border-slate-700 pt-3" data-testid="tac-content">
                          <p className="font-semibold text-sm text-slate-800 dark:text-slate-200">Refund Policy</p>
                          <p>At Tutors Melbourne, we keep our group classes small (max 5 students) so every student gets proper attention. Because of this limited capacity and the preparation involved, we have the following refund policy:</p>
                          <p><strong>Cooling-off Period:</strong> You have 7 days from the date of purchase to cancel your group class package for a full refund, minus any classes already attended.</p>
                          <p><strong>After the 7-day cooling-off period:</strong></p>
                          <ul className="list-disc pl-5 space-y-1">
                            <li>No refunds will be issued for change of mind.</li>
                            <li>No refunds for missed classes (no-shows).</li>
                          </ul>
                          <p><strong>Cancellations & Make-up Classes:</strong> Students must give at least 24 hours' notice to cancel or reschedule a group class. Missed classes with less than 24 hours' notice will be forfeited with no credit or refund. We may offer make-up classes subject to availability.</p>
                          <p><strong>How to request a refund:</strong> Email hello@tutorsmelbourne.com.au with your child's name, class details, and reason. Valid refund requests will be processed within 5 business days.</p>
                          <p className="font-semibold text-sm text-slate-800 dark:text-slate-200 pt-2">Terms & Conditions – Group Classes</p>
                          <p>By enrolling in a group class at Tutors Melbourne, you agree to the following terms:</p>
                          <ol className="list-decimal pl-5 space-y-2">
                            <li><strong>Group Class Format:</strong> Classes are held on a fixed weekly schedule. Start and end dates are clearly communicated at the time of enrolment.</li>
                            <li><strong>Payments:</strong> Full payment (or deposit as agreed) is required to secure a spot in the group class. Places are not confirmed until payment is received.</li>
                            <li><strong>Code of Conduct:</strong> Students are expected to attend classes on time, participate respectfully, and treat both tutors and fellow students with courtesy. Persistent disruptive behaviour may result in removal from the group class without refund.</li>
                            <li><strong>Materials & Intellectual Property:</strong> All lesson materials, resources, and work provided remain the property of Tutors Melbourne and are for the enrolled student's personal use only.</li>
                            <li><strong>Academic Outcomes:</strong> While we strive to deliver high-quality tutoring, we cannot guarantee specific academic results. Progress depends on the student's attendance, effort, and application of the material.</li>
                            <li><strong>Changes to Schedule:</strong> We reserve the right to change class times or tutors with reasonable notice. If a major change occurs, you will be offered the option to transfer or pause your enrolment.</li>
                            <li><strong>Privacy:</strong> Your personal information will only be used for delivering the group classes and will not be shared with third parties without your consent (except as required by law).</li>
                          </ol>
                        </div>
                      )}
                    </div>
                    <div className="flex items-start gap-3 mb-5">
                      <Checkbox id="accept-tac" checked={acceptedTac} onCheckedChange={setAcceptedTac}
                        className="mt-0.5 border-brand-purple data-[state=checked]:bg-brand-purple data-[state=checked]:border-brand-purple"
                        data-testid="tac-checkbox" />
                      <label htmlFor="accept-tac" className="text-sm text-slate-600 dark:text-slate-400 font-body cursor-pointer leading-snug">
                        I have read and accept the <button type="button" onClick={() => setTacExpanded(true)} className="text-brand-purple dark:text-brand-purple-light font-semibold underline">Terms & Conditions</button> and Refund Policy
                      </label>
                    </div>
                    <Button type="submit" disabled={submitting || spotsLeft <= 0 || !acceptedTac}
                      className="w-full bg-brand-gold hover:bg-brand-gold-hover text-white rounded-full py-6 font-body font-bold text-lg shadow-lg hover:shadow-xl transition-all duration-300"
                      data-testid="proceed-to-payment-btn">
                      {submitting ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Processing...</> : `Proceed to Payment - $${(cls.price * 1.1).toFixed(2)} AUD (incl. GST)`}
                    </Button>
                    <p className="text-xs text-slate-400 font-body text-center mt-3">Secure payment powered by Stripe</p>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
